import React from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, Phone, Search, X } from "lucide-react";
import { SERVICES, CITIES, BUSINESS, NAV } from "@/data/content";
import { SectionHeading, CTA, FinalCTA } from "@/components/Shared";
import { SEO, breadcrumbSchema, serviceSchema } from "@/components/SEO";

// Service × Location hyper-local page (72 combos from 6 main services × 12 cities)
// URL pattern: /services/:service/in/:city
export function ServiceLocationPage() {
  const { service: serviceSlug, city: citySlug } = useParams();
  const service = SERVICES.find((s) => s.slug === serviceSlug);
  const city = CITIES.find((c) => c.slug === citySlug);

  if (!service || !city) {
    return (
      <div className="py-24 container-x text-center">
        <h1 className="font-heading text-4xl font-extrabold">Page not found</h1>
        <Link to="/services" className="btn-primary mt-8">Back to Services <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }

  // Unique H1 per combo
  const h1 = `${service.name} in ${city.name}, MI`;
  const title = `${service.name} ${city.name}, MI`;
  const desc = `${service.name} in ${city.name}, Michigan by Goral Construction. Licensed, insured, 28+ yrs experience. Free estimate - call ${BUSINESS.phone}.`;

  // Build unique intro combining service + city
  const intro = `Goral Construction LLC provides ${service.name.toLowerCase()} for homeowners in ${city.name}, Michigan. ${city.blurb} ${city.highlight} ${service.intro}`;

  const relatedCities = CITIES.filter((c) => c.slug !== citySlug).slice(0, 6);
  const relatedServices = SERVICES.filter((s) => s.slug !== serviceSlug).slice(0, 4);

  return (
    <div>
      <SEO
        title={title}
        description={desc}
        path={`/services/${serviceSlug}/in/${citySlug}`}
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: service.name, path: `/services/${service.slug}` },
          { name: city.name, path: `/services/${service.slug}/in/${city.slug}` },
        ])}
      />

      <section className="bg-bg-dark text-white py-16 md:py-24 relative overflow-hidden group">
        {/* Dark skyline accent - subtly brightens on hover */}
        <svg aria-hidden="true" className="absolute -right-12 bottom-0 w-[460px] h-[180px] opacity-[0.05] group-hover:opacity-[0.14] transition-opacity duration-700 pointer-events-none" viewBox="0 0 460 180" fill="none" stroke="currentColor" strokeWidth="1">
          <path d="M20 160 L60 120 L100 160 L100 180 L20 180 Z" />
          <path d="M120 160 L170 100 L220 160 L220 180 L120 180 Z" />
          <path d="M240 160 L280 130 L320 160 L320 180 L240 180 Z" />
          <path d="M340 160 L400 100 L460 160 L460 180 L340 180 Z" />
          <rect x="165" y="140" width="12" height="16" />
          <rect x="395" y="140" width="12" height="16" />
        </svg>
        <div className="container-x relative">
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Link to="/services" className="hover:text-brand">Services</Link>
            <span>/</span>
            <Link to={`/services/${service.slug}`} className="hover:text-brand">{service.name}</Link>
            <span>/</span>
            <span className="text-white">{city.name}</span>
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tight mt-5 max-w-4xl leading-[1.02]">{h1}</h1>
          <p className="text-white/85 text-base md:text-lg max-w-3xl mt-5 leading-relaxed">{intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/get-estimate" className="btn-primary" data-testid="sloc-estimate-cta">Get free {service.name.toLowerCase()} estimate <ArrowRight className="w-4 h-4" /></Link>
            <a href={`tel:${BUSINESS.phoneTel}`} className="btn-inverted"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container-x grid lg:grid-cols-3 gap-10 lg:gap-16">
          <div className="lg:col-span-2">
            <div className="overline text-brand">Local service</div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-2">Why {city.name} homeowners choose Goral for {service.name.toLowerCase()}</h2>
            <div className="mt-6 space-y-5 text-ink-soft leading-relaxed">
              <p>Goral Construction LLC has served {city.name} and the surrounding Oakland County communities since 2010. Maciej Ogonowski, the owner, has 28+ years of hands-on Michigan roofing experience. Every {service.name.toLowerCase()} project in {city.name} is handled by our own crew - we do not use subcontractors.</p>
              <p>{city.highlight} That's why our installations in {city.name} are built to a higher spec than the minimum code.</p>
              <p>Our {service.name.toLowerCase()} work in {city.name} comes with our standard 10-year workmanship warranty, full manufacturer warranty registration, and a free final-walkthrough inspection. Homeowners pay only once the job passes final inspection.</p>
            </div>

            <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-12">What's included in {service.name.toLowerCase()}</h2>
            <ul className="mt-6 space-y-3">
              {service.features.map((f) => (
                <li key={f} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                  <span className="text-ink-soft leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>

            {service.faqs && service.faqs.length > 0 && (
              <>
                <h2 className="font-heading text-3xl md:text-4xl font-extrabold mt-12">{service.name} FAQs for {city.name} homeowners</h2>
                <div className="mt-6 space-y-5">
                  {service.faqs.slice(0, 4).map((f) => (
                    <div key={f.q} className="border-l-2 border-brand pl-5">
                      <div className="font-heading text-lg font-bold">{f.q}</div>
                      <div className="text-ink-soft mt-2 leading-relaxed">{f.a}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="bg-bg-soft border border-ink-line/60 p-6 sticky top-28">
              <div className="overline text-brand">Service area</div>
              <div className="font-heading text-xl font-bold mt-2 flex items-center gap-2"><MapPin className="w-5 h-5 text-brand" /> {city.name}, MI</div>
              <div className="text-sm text-ink-soft mt-2 leading-relaxed">{city.blurb}</div>

              <div className="mt-6 pt-6 border-t border-ink-line/60">
                <div className="overline text-ink-soft">Service</div>
                <div className="font-heading text-lg font-bold mt-1">{service.name}</div>
                <div className="text-sm text-ink-soft mt-1">{service.short}</div>
              </div>

              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-primary w-full justify-center mt-6" data-testid="sloc-sidebar-call">
                <Phone className="w-4 h-4" /> Call for {city.name} estimate
              </a>
              <Link to="/get-estimate" className="btn-secondary w-full justify-center mt-3">
                Request online <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-bg-soft border-y border-ink-line">
        <div className="container-x">
          <div className="overline text-brand">Also serving</div>
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold mt-2">{service.name} in nearby Oakland County cities</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {relatedCities.map((c) => (
              <Link
                key={c.slug}
                to={`/services/${service.slug}/in/${c.slug}`}
                className="px-4 py-2 bg-white border border-ink-line hover:border-brand hover:text-brand text-sm font-semibold rounded-sm transition-colors"
                data-testid={`sloc-related-${c.slug}`}
              >
                {service.name} in {c.name}
              </Link>
            ))}
          </div>

          <div className="overline text-brand mt-10">Related services</div>
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold mt-2">Other services we provide in {city.name}</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {relatedServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}/in/${city.slug}`}
                className="px-4 py-2 bg-white border border-ink-line hover:border-brand hover:text-brand text-sm font-semibold rounded-sm transition-colors"
              >
                {s.name} in {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

// Lightweight slug similarity scorer (token overlap + substring) - no deps.
function scoreMatch(a, b) {
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const at = norm(a).split(" ").filter(Boolean);
  const bt = norm(b).split(" ").filter(Boolean);
  if (!at.length || !bt.length) return 0;
  const set = new Set(at);
  const overlap = bt.filter((t) => set.has(t)).length;
  const sub = norm(a).includes(norm(b)) || norm(b).includes(norm(a)) ? 1 : 0;
  return overlap * 2 + sub;
}

function suggestRoutes(badPath) {
  const allRoutes = [
    { label: "Home", to: "/" },
    { label: "All Services", to: "/services" },
    { label: "All Service Areas", to: "/service-areas" },
    { label: "All Materials", to: "/materials" },
    { label: "Our Work", to: "/our-work" },
    { label: "Reviews", to: "/reviews" },
    { label: "Blog", to: "/blog" },
    { label: "Free Estimate", to: "/get-estimate" },
    { label: "Roof Cost Calculator", to: "/roof-cost-calculator" },
    { label: "Contact", to: "/contact" },
    ...SERVICES.map((s) => ({ label: s.name, to: `/services/${s.slug}` })),
    ...CITIES.map((c) => ({ label: `Roofing in ${c.name}, MI`, to: `/service-areas/${c.slug}` })),
  ];
  return allRoutes
    .map((r) => ({ ...r, _s: scoreMatch(r.to + " " + r.label, badPath) }))
    .sort((a, b) => b._s - a._s)
    .slice(0, 3);
}

// Custom 404 with smart suggestions + "moved from old site" toast
export function NotFoundPage() {
  const loc = useLocation();
  const [query, setQuery] = React.useState("");
  const [showOldSiteToast, setShowOldSiteToast] = React.useState(false);

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const ref = document.referrer || "";
    if (/goralconstructionllc\.com/i.test(ref)) {
      setShowOldSiteToast(true);
    }
  }, []);

  const suggestions = suggestRoutes(loc.pathname);

  const onSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    // Send users straight to the closest match
    const top = suggestRoutes(query)[0];
    if (top) window.location.assign(top.to);
  };

  return (
    <div className="min-h-[70vh] flex items-center py-20 relative">
      {showOldSiteToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 max-w-md w-[92%]">
          <div className="bg-ink text-white px-5 py-4 rounded-sm shadow-2xl border border-brand/40 flex items-start gap-3" data-testid="old-site-toast">
            <div className="flex-1">
              <div className="font-heading text-sm font-extrabold text-brand uppercase tracking-wider">Welcome from goralconstructionllc.com</div>
              <div className="text-sm text-white/90 mt-1 leading-snug">We have moved to <strong className="text-white">roofing-goral.com</strong>. Same Goral Construction team, same Master Certified crew. Pick a link below or call {BUSINESS.phone}.</div>
            </div>
            <button onClick={() => setShowOldSiteToast(false)} aria-label="Close" className="text-white/60 hover:text-white" data-testid="dismiss-old-site-toast">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="container-x text-center max-w-2xl mx-auto">
        <div className="overline text-brand">404</div>
        <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-3">Page not found.</h1>
        <p className="text-ink-soft text-base md:text-lg mt-5 leading-relaxed">
          {showOldSiteToast
            ? "Looks like you came from our old site. The page you wanted has moved - here are the closest matches:"
            : "That URL doesn't exist on our site - but don't worry. If you're looking for a roof estimate, we can help right now."}
        </p>

        {suggestions.length > 0 && (
          <div className="mt-8 grid sm:grid-cols-3 gap-3">
            {suggestions.map((s) => (
              <Link key={s.to} to={s.to} className="card-base p-4 text-left hover:border-brand transition-colors group" data-testid={`404-suggestion-${s.to.replace(/\//g, '-')}`}>
                <div className="overline text-brand">Did you mean</div>
                <div className="font-heading text-base font-bold mt-1 group-hover:text-brand transition-colors">{s.label}</div>
                <div className="text-xs text-ink-soft mt-1 break-all">{s.to}</div>
              </Link>
            ))}
          </div>
        )}

        <form onSubmit={onSearch} className="mt-8 max-w-md mx-auto flex border border-ink-line rounded-sm overflow-hidden" data-testid="404-search-form">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the site... (e.g. waterford, gutters, repair)"
            className="flex-1 px-4 py-3 outline-none text-sm"
            data-testid="404-search-input"
          />
          <button type="submit" className="bg-ink text-white px-4 hover:bg-brand transition-colors" data-testid="404-search-submit">
            <Search className="w-4 h-4" />
          </button>
        </form>

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/get-estimate" className="btn-primary">Get free estimate <ArrowRight className="w-4 h-4" /></Link>
          <a href={`tel:${BUSINESS.phoneTel}`} className="btn-secondary"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
        </div>

        <div className="mt-12 text-sm text-ink-soft">
          <div className="overline">Quick links</div>
          <div className="mt-3 flex flex-wrap gap-3 justify-center">
            <Link to="/services" className="underline hover:text-brand">Services</Link>
            <Link to="/materials" className="underline hover:text-brand">Materials</Link>
            <Link to="/service-areas" className="underline hover:text-brand">Service areas</Link>
            <Link to="/our-work" className="underline hover:text-brand">Our work</Link>
            <Link to="/reviews" className="underline hover:text-brand">Reviews</Link>
            <Link to="/blog" className="underline hover:text-brand">Blog</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Thank you page (noindex) - used after Web3Forms submission
export function ThankYouPage() {
  return (
    <div className="min-h-[70vh] flex items-center py-20">
      <SEO
        title="Thank You | Goral Construction LLC"
        description="Your request was received. We'll be in touch shortly."
        path="/thank-you"
        noindex
      />
      <div className="container-x text-center max-w-2xl mx-auto">
        <div className="w-20 h-20 bg-brand/10 text-brand rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <div className="overline text-brand mt-6">Message received</div>
        <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight mt-3">Thanks - we'll call you within 24 hours.</h1>
        <p className="text-ink-soft text-base md:text-lg mt-5 leading-relaxed">Your estimate request has been received. A member of the Goral Construction team will call you back within 24 business hours (often sooner). If your roof is actively leaking, please call us directly.</p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <a href={`tel:${BUSINESS.phoneTel}`} className="btn-primary"><Phone className="w-4 h-4" /> Call now: {BUSINESS.phone}</a>
          <Link to="/" className="btn-secondary">Back home</Link>
        </div>
      </div>
    </div>
  );
}
